import { ColDef } from 'ag-grid-community';
import { CurrencyDollar } from 'react-bootstrap-icons';
import { BaseReport } from './BaseReport';
import { ReportConfig, FilterField } from './types';
import { PaymentReport as PaymentReportType, PaymentReportApiResponse } from '../Data/ApiTypes';
import { Api } from '../Data/Api';
import { showMoney, cents } from '../Data/Money';
import { toLocalDateTime } from '../Data/Date';
import { ServerTransferSavedReportResponse  } from '../shared/api/axios-client';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import Multiselect from 'react-select';
import { Button } from '../Controls/Button';
import { ColDefWithCurrency } from './AgingReport';

interface PaymentFilter {
    page: number;
    pageSize: number;
    facilityId?: number;
    facilityIds?: number[];
    email?: string;
    name?: string;
    from?: string;
    to?: string;
    paymentType?: string;
    paymentStatus?: string;
}

const getSingleFacilityColumns = (): ColDefWithCurrency<PaymentReportType>[] => [
    { field: 'name', headerName: 'Parker Name', hide: false, filter: false },
    { field: 'emailAddress', headerName: 'Email', hide: false, filter: false },
    { 
        field: 'totalPaid',
        headerName: 'Total Revenue',
        hide: false,
        filter: false,
        valueFormatter: params => params.value ? showMoney(params.value) : '-',
        isCurrency: true, 
    },
    { 
        field: 'facilityFees',
        headerName: 'Facility Fees',
        hide: false,
        filter: false,
        valueFormatter: params => params.value ? showMoney(params.value) : '-',
        isCurrency: true, 
    },
    { 
        field: 'remittedToFacility',
        headerName: 'Remitted To Facility',
        hide: false,
        filter: false,
        valueFormatter: params => params.value ? showMoney(params.value) : '-',
        isCurrency: true, 
    },
    { 
        field: 'paidWhen',
        headerName: 'Payment Date',
        hide: false,
        filter: false,
        valueFormatter: (p) => toLocalDateTime(p.value),
    },
    { field: 'status', headerName: 'Status', hide: false, filter: false },
    { field: 'payoutWhen', headerName: 'Payout Date', hide: false, filter: false,
        valueFormatter: (p) => toLocalDateTime(p.value),
    },
    { field: 'paymentId', headerName: 'Payment #', type: 'rightAligned', hide: true, filter: false },
    { 
        field: 'reversed',
        headerName: 'Reversed',
        hide: true,
        filter: false,
        valueFormatter: params => params.value ? showMoney(params.value) : '-',
        isCurrency: true, 
    },
    { field: 'reversedReason', headerName: 'Reversed Reason', hide: false, filter: false },
    { field: 'reversedWhen', headerName: 'Reversed Date', hide: true, filter: false,
        valueFormatter: (p) => toLocalDateTime(p.value),
    },
    { field: 'paymentType', headerName: 'Payment Type(s)', hide: true, filter: false },
    { field: 'orderType', headerName: 'Order Type', hide: true, filter: false },
    { field: 'paymentIntentKey', headerName: 'Receipt #', hide: true, filter: false }
];

const getMultiFacilityColumns = (): ColDef<PaymentReportType>[] => [
    { 
        field: 'facilityName', 
        headerName: 'Facility', 
        hide: false, 
        filter: false,
        pinned: 'left' // Pin to left for better visibility when scrolling
    },
    ...getSingleFacilityColumns()
];

const filterFields: FilterField[] = [
    { name: 'email', label: 'Email', type: 'text', placeholder: 'Filter by email' },
    { name: 'name', label: 'Parker Name', type: 'text', placeholder: 'Filter by name' },
    { 
        name: 'paymentType', 
        label: 'Payment Type', 
        type: 'multiselect',
        options: [
            { value: 'Card', label: 'Credit Card' },
            { value: 'ACH', label: 'ACH' },
            { value: 'Cash', label: 'Cash' },
            { value: 'Check', label: 'Check' },
            { value: 'Imported', label: 'Imported' }
        ]
    },
    { 
        name: 'paymentStatus', 
        label: 'Status', 
        type: 'select',
        options: [
            { value: 'cleared', label: 'Cleared' },
            { value: 'failed', label: 'ACH Failure' },
            { value: 'reversed', label: 'Reversed' },
            { value: 'processing', label: 'Processing' }
        ]
    }
];

const createConfig = (facilityIds?: number[], facilities?: any[]): ReportConfig<PaymentReportType, PaymentFilter> => {
    const isMultiFacility = facilityIds !== undefined;
    
    return {
        id: 'payment',
        title: 'Revenue Report',
        icon: <CurrencyDollar style={{ fontSize: '1.5rem', marginTop:'-10px' }} />,
        description: `<p>This report provides a detailed ledger of all payments processed in the system—one line per transaction—so you can reconcile revenue, fees, and refunds for each parker and facility.</p>
            <p>You'll see:</p>
            <ul>
                <li>The total amount paid and how it's split between parker fees and facility/operator fees</li>
                <li>Any reversals or refunds that have been applied</li>
                <li>Payment methods, statuses, and the date/time of each transaction</li>
            </ul>`,
        defaultColumns: isMultiFacility ? getMultiFacilityColumns() : getSingleFacilityColumns(),
        filterFields,
        facilities, // Include facilities data in the config
        defaultFilterForm: {
            email: '',
            name: '',
            from: '',
            to: '',
            paymentType: '',
            paymentStatus: '',
            quickDateMode: 'All',
            quickPayoutDateMode: 'All',
            payoutFrom: '',
            payoutTo: '',
            age: ''
        },
        getData: async (filter) => {
            if (isMultiFacility) {
                // Pass selected facilities to the API
                const response = await Api.paymentReports({
                    ...filter,
                    facilityId: undefined, // Remove single facility filter
                    facilityIds: facilityIds && facilityIds.length > 0 ? facilityIds : undefined // Only pass IDs if we have some
                });
                if (!response.isOk) throw new Error(response.error);
                return response.value;
            } else {
                const response = await Api.paymentReports(filter);
                if (!response.isOk) throw new Error(response.error);
                return response.value;
            }
        },
        transformData: (data: PaymentReportApiResponse[]) => data.map(report => {
            // Ensure facilityName is populated for multi-facility reports
            let facilityName = report.facilityName;
            if (isMultiFacility && (!facilityName || facilityName.trim() === '') && facilities) {
                const facility = facilities.find(f => f.facilityId === report.facilityId);
                facilityName = facility?.name || `Facility ${report.facilityId}`;
            }
            
            return {
                ...report,
                facilityName,
                totalPaid: cents(report.totalPaid),
                facilityFees: cents(report.facilityFees),
                reversed: cents(report.reversed),
                remittedToFacility: cents(report.remittedToFacility)
            };
        })
    };
};

export function PaymentReport({
    facilityId,
    facilityIds,
    facilities,
    savedReports,
    setSavedReports,
    heightOffset = 50
}: {
    facilityId?: number;
    facilityIds?: number[];
    facilities?: any[]; // Pre-loaded facilities for multi-facility mode
    savedReports: ServerTransferSavedReportResponse [];
    setSavedReports: React.Dispatch<React.SetStateAction<ServerTransferSavedReportResponse []>>;
    heightOffset?: number;
}) {
    const [selectedFacilities, setSelectedFacilities] = useState<any[]>([]);
    const [currentFacilityIds, setCurrentFacilityIds] = useState<number[]>(facilityIds || []);
    const isMultiFacility = facilityIds !== undefined;

    // Handle facilityIds changes from saved reports
    const handleFacilityIdsChange = useCallback((newFacilityIds: number[]) => {
        if (isMultiFacility && facilities) {
            const options = facilities.map(facility => ({
                label: facility.name,
                value: facility.facilityId
            }));
            
            // Update selected facilities based on new facilityIds
            const selectedOptions = options.filter(option => 
                newFacilityIds.includes(option.value)
            );
            setSelectedFacilities(selectedOptions);
            setCurrentFacilityIds(newFacilityIds);
        }
    }, [isMultiFacility, facilities]);

    // Handle reset to clear facility selection
    const handleReset = useCallback(() => {
        if (isMultiFacility) {
            setSelectedFacilities([]);
            setCurrentFacilityIds([]);
        }
    }, [isMultiFacility]);

    // Set up facility options when facilities are provided
    useEffect(() => {
        if (isMultiFacility && facilities) {
            const options = facilities.map(facility => ({
                label: facility.name,
                value: facility.facilityId
            }));
            
            // Set selected facilities based on facilityIds prop
            const selectedOptions = options.filter(option => 
                facilityIds?.includes(option.value)
            );
            setSelectedFacilities(selectedOptions);
        }
    }, [isMultiFacility, facilityIds, facilities]);

    // Update current facility IDs when selection changes
    useEffect(() => {
        if (isMultiFacility) {
            const newIds = selectedFacilities.map(option => option.value);
            setCurrentFacilityIds(newIds);
        }
    }, [selectedFacilities, isMultiFacility]);

    const facilityOptions = useMemo(() => 
        (facilities || []).map(facility => ({
            label: facility.name,
            value: facility.facilityId
        })), 
        [facilities]
    );

    const handleFacilityChange = (selectedOptions: any) => {
        setSelectedFacilities(selectedOptions || []);
    };

    const selectAllFacilities = () => {
        setSelectedFacilities(facilityOptions);
    };

    const clearAllFacilities = () => {
        setSelectedFacilities([]);
    };

    const config = createConfig(isMultiFacility ? currentFacilityIds : undefined, facilities);

    return (
        <>
            {isMultiFacility && (
                <Row className="mb-3">
                    <Col>
                        <Form.Group>
                            <div className="d-flex align-items-center mb-2 flex-nowrap">
                                <Form.Label className="mb-0 me-3 flex-shrink-0">Facilities</Form.Label>
                                {facilityOptions.length > 1 && (
                                    <div className="flex-shrink-0 me-2">
                                        <Button 
                                            type="button" 
                                            size="sm" 
                                            variant="outline-primary" 
                                            className="me-1"
                                            onClick={selectAllFacilities}
                                        >
                                            Select All
                                        </Button>
                                        <Button 
                                            type="button" 
                                            size="sm" 
                                            variant="outline-secondary" 
                                            onClick={clearAllFacilities}
                                        >
                                            None
                                        </Button>
                                    </div>
                                )}
                                <div className="flex-grow-1 min-w-0">
                                    <Multiselect 
                                        onChange={handleFacilityChange}
                                        value={selectedFacilities}
                                        isMulti 
                                        options={facilityOptions}
                                        placeholder="Select facilities..."
                                        isClearable={false}
                                    />
                                </div>
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
            )}
            <BaseReport
                facilityId={facilityId}
                facilityIds={isMultiFacility ? currentFacilityIds : undefined}
                config={config}
                savedReports={savedReports}
                setSavedReports={setSavedReports}
                onFacilityIdsChange={handleFacilityIdsChange}
                onReset={handleReset}
                heightOffset={heightOffset}
                storageKey='report_columns_payment'
            />
        </>
    );
}
